import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { X, User, UserPlus, Store, Plus, Trash2, ArrowLeft, ImagePlus, Copy } from "lucide-react";

/**
 * iter64 Phase B · "+ Create Order" modal.
 *
 * Step 1 · 3-card picker "Who is this order for?"
 * Step 2 · type-specific form + multi-line item builder
 *
 * Uses POST /admin/orders/unified — backend handles the 3 attribution
 * variants (Member Profile · Guest Member · Golf Shop) via Pydantic
 * discrimination on order_type.
 */
const TYPE_CARDS = [
  { value: "Member Profile Order", label: "Member With Profile",
    icon: User, blurb: "The member already has a My Crest Club account." },
  { value: "Guest Member Order", label: "Member Without Profile",
    icon: UserPlus, blurb: "The member is a club member but not registered on My Crest Club." },
  { value: "Golf Shop Order", label: "Golf Shop / Club Use",
    icon: Store, blurb: "Inventory, supplies, tournaments, uniforms, or other club-use purchases." },
];

export default function CreateOrderModal({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [orderType, setOrderType] = useState(null);
  const [options, setOptions] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1); setOrderType(null);
    api.get("/admin/orders/options").then(({ data }) => setOptions(data)).catch(() => {});
    api.get("/admin/members", { params: { limit: 100 } })
      .then(({ data }) => setMembers(data || []))
      .catch(() => setMembers([]));
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="create-order-modal">
      <div className="bg-white w-full max-w-3xl border border-[#E5E3D8] max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#F0EDE0]">
          <div>
            <div className="overline">Create Order</div>
            <h2 className="font-serif text-2xl text-[#1A422D] mt-1">
              {step === 1 ? "Who is this order for?" : (TYPE_CARDS.find((c) => c.value === orderType)?.label || "Order Details")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="create-order-close"
            className="text-[#7A7A7A] hover:text-[#1A422D]"
          >
            <X size={20} />
          </button>
        </div>

        {step === 1 ? (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="create-order-picker">
            {TYPE_CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => { setOrderType(c.value); setStep(2); }}
                  data-testid={`order-type-card-${c.value.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-left p-5 border border-[#E5E3D8] bg-white hover:border-[#1A422D] hover:bg-[#F9F8F4] transition-colors"
                >
                  <Icon className="text-[#B59A5A]" size={22} />
                  <div className="mt-3 font-serif text-lg text-[#1A422D]">{c.label}</div>
                  <div className="mt-2 text-xs text-[#4A4A4A] leading-relaxed">{c.blurb}</div>
                </button>
              );
            })}
          </div>
        ) : (
          <OrderForm
            orderType={orderType}
            options={options}
            members={members}
            memberQuery={memberQuery}
            setMemberQuery={setMemberQuery}
            onBack={() => setStep(1)}
            onCreated={onCreated}
          />
        )}
      </div>
    </div>
  );
}

// ============ Order Form ==================================================

function OrderForm({ orderType, options, members, memberQuery, setMemberQuery, onBack, onCreated }) {
  const [items, setItems] = useState([{ item_name: "", quantity: 1, unit_cost: "", size: "", color: "", photos: [] }]);
  const [memberId, setMemberId] = useState("");
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileInputRefs = useRef({});
  const [guestForm, setGuestForm] = useState({
    member_name: "", member_number: "", email: "", phone: "", locker_number: "",
    where_seen: "", where_seen_other: "",
  });
  const [duplicates, setDuplicates] = useState(null);
  const [ignoreDupes, setIgnoreDupes] = useState(false);
  const [gs, setGS] = useState({
    order_purpose: (options?.order_purposes || [])[0] || "Golf Shop Inventory",
    department: "Golf Shop",
    po_number: "",
    budget_code: "",
    internal_approval_required: false,
  });
  const [common, setCommon] = useState({
    vendor_notes: "",
    staff_internal_note: "",
    requested_delivery_date: "",
    submission_mode: "submit_on_behalf",
    // iter67 · Order-level taxonomy + Vendor Packet privacy toggle.
    category: "",
    subcategory: "",
    include_member_info_on_packet: true,
  });
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const filteredMembers = useMemo(() => {
    if (!memberQuery.trim()) return members.slice(0, 20);
    const q = memberQuery.toLowerCase();
    return members.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.membership_number || "").toLowerCase().includes(q),
    ).slice(0, 20);
  }, [members, memberQuery]);

  const runDuplicateCheck = async () => {
    if (!guestForm.member_name && !guestForm.member_number && !guestForm.email && !guestForm.phone) {
      setDuplicates(null); return;
    }
    try {
      const { data } = await api.post("/admin/unregistered-members/check-duplicates", {
        member_name: guestForm.member_name,
        member_number: guestForm.member_number,
        email: guestForm.email,
        phone: guestForm.phone,
      });
      setDuplicates(data);
    } catch (_e) { setDuplicates(null); }
  };

  const addItem = () => setItems([...items, { item_name: "", quantity: 1, unit_cost: "", size: "", color: "", photos: [] }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  // iter67 §10 · Client-side duplicate — clone all fields (photos optional
  // via the second arg) and splice the clone directly after the source row.
  const duplicateItem = (idx, { copyPhotos = true } = {}) => {
    setItems((arr) => {
      const src = arr[idx];
      if (!src) return arr;
      const { photos, ...rest } = src;
      const clone = { ...rest, photos: copyPhotos ? [...(photos || [])] : [] };
      return [...arr.slice(0, idx + 1), clone, ...arr.slice(idx + 1)];
    });
  };

  const MAX_PHOTOS_PER_ITEM = 3;

  const uploadFilesForItem = async (idx, fileList) => {
    if (!fileList || fileList.length === 0) return;
    const current = items[idx]?.photos || [];
    const room = MAX_PHOTOS_PER_ITEM - current.length;
    if (room <= 0) {
      setError(`Each item can have at most ${MAX_PHOTOS_PER_ITEM} photos.`);
      return;
    }
    setError(null);
    setUploadingIdx(idx);
    try {
      const files = Array.from(fileList).slice(0, room);
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      }
      setItems((arr) => arr.map((it, i) =>
        i === idx ? { ...it, photos: [...(it.photos || []), ...uploaded] } : it,
      ));
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Upload failed.");
    } finally {
      setUploadingIdx(null);
      const ref = fileInputRefs.current[idx];
      if (ref) ref.value = "";
    }
  };

  const removeItemPhoto = (idx, photoId) => {
    setItems((arr) => arr.map((it, i) =>
      i === idx ? { ...it, photos: (it.photos || []).filter((p) => p.photo_id !== photoId) } : it,
    ));
  };

  const runningTotal = useMemo(() => items.reduce((s, it) => {
    const q = Number(it.quantity) || 0;
    const c = Number(it.unit_cost) || 0;
    return s + q * c;
  }, 0), [items]);

  const canSubmit = () => {
    if (items.some((it) => !it.item_name.trim())) return false;
    if (orderType === "Member Profile Order" && !memberId) return false;
    if (orderType === "Guest Member Order") {
      if (!guestForm.member_name.trim() && !guestForm.member_number.trim()) return false;
    }
    if (orderType === "Golf Shop Order" && !gs.order_purpose) return false;
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) { setError("Please complete required fields."); return; }
    setBusy(true); setError(null);
    try {
      const body = {
        order_type: orderType,
        items: items.map((it) => ({
          item_name: it.item_name.trim(),
          quantity: Number(it.quantity) || 1,
          unit_cost: it.unit_cost ? Number(it.unit_cost) : undefined,
          size: it.size || undefined,
          color: it.color || undefined,
          brand: it.brand || undefined,
          sku: it.sku || undefined,
          style_number: it.style_number || undefined,
          description: it.description || undefined,
          logo_preference: it.logo_preference || undefined,
          logo_location: it.logo_location || undefined,
          logo_color: it.logo_color || undefined,
          thread_color: it.thread_color || undefined,
          photo_ids: (it.photos || []).map((p) => p.photo_id),
        })).map((o) => {
          if (Array.isArray(o.photo_ids) && o.photo_ids.length === 0) delete o.photo_ids;
          Object.keys(o).forEach((k) => { if (o[k] === undefined || o[k] === "") delete o[k]; });
          return o;
        }),
        vendor_notes: common.vendor_notes.trim() || undefined,
        staff_internal_note: common.staff_internal_note.trim() || undefined,
        requested_delivery_date: common.requested_delivery_date || undefined,
        submission_mode: common.submission_mode,
        category: common.category || undefined,
        subcategory: common.subcategory || undefined,
        include_member_info_on_packet: common.include_member_info_on_packet,
      };
      if (orderType === "Member Profile Order") body.member_id = memberId;
      if (orderType === "Guest Member Order") {
        body.guest_member = {
          member_name: guestForm.member_name.trim() || undefined,
          member_number: guestForm.member_number.trim() || undefined,
          email: guestForm.email.trim() || undefined,
          phone: guestForm.phone.trim() || undefined,
          locker_number: guestForm.locker_number.trim() || undefined,
          where_seen: guestForm.where_seen || undefined,
          where_seen_other: guestForm.where_seen === "Other" ? (guestForm.where_seen_other.trim() || undefined) : undefined,
          ignore_duplicates: ignoreDupes,
        };
      }
      if (orderType === "Golf Shop Order") {
        body.order_purpose = gs.order_purpose;
        body.department = gs.department || undefined;
        body.po_number = gs.po_number.trim() || undefined;
        body.budget_code = gs.budget_code.trim() || undefined;
        body.internal_approval_required = gs.internal_approval_required;
      }
      Object.keys(body).forEach((k) => { if (body[k] === undefined || body[k] === "") delete body[k]; });
      const { data } = await api.post("/admin/orders/unified", body);
      onCreated?.(data);
    } catch (err) {
      setError((formatApiErrorDetail(err?.response?.data?.detail) || err?.message) || "Could not create order.");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6" data-testid="create-order-form">
      <button type="button" onClick={onBack} data-testid="create-order-back"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D] mb-4">
        <ArrowLeft size={13} /> Back to picker
      </button>

      {/* Type-specific attribution */}
      {orderType === "Member Profile Order" && (
        <div className="mb-6" data-testid="attribution-member">
          <label className="label-elegant block mb-1">Search Member</label>
          <input
            type="text" value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)}
            placeholder="Search by name, email, or member number"
            data-testid="member-search" className="input-field"
          />
          <div className="mt-2 max-h-48 overflow-y-auto border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]">
            {filteredMembers.map((m) => (
              <button
                key={m.user_id}
                type="button"
                onClick={() => setMemberId(m.user_id)}
                data-testid={`member-choice-${m.user_id}`}
                className={`w-full text-left p-2 hover:bg-[#F9F8F4] transition-colors ${memberId === m.user_id ? "bg-[#F9F8F4] border-l-2 border-[#1A422D]" : ""}`}
              >
                <div className="text-sm text-[#1A422D]">{m.name}</div>
                <div className="text-xs text-[#7A7A7A]">{m.email} · #{m.membership_number || "—"}</div>
              </button>
            ))}
            {filteredMembers.length === 0 && (
              <div className="p-3 text-xs text-[#7A7A7A]">No matches</div>
            )}
          </div>
        </div>
      )}

      {orderType === "Guest Member Order" && (
        <div className="mb-6 space-y-3" data-testid="attribution-guest">
          <div className="p-3 bg-[#F9F8F4] border border-[#F0EDE0] text-xs text-[#4A4A4A]">
            No My Crest Club profile is required for this order. Enter what you know — a name and/or member number is enough.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-elegant block mb-1">Member Name</label>
              <input value={guestForm.member_name} onBlur={runDuplicateCheck}
                     onChange={(e) => setGuestForm({ ...guestForm, member_name: e.target.value })}
                     data-testid="guest-name" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Member Number</label>
              <input value={guestForm.member_number} onBlur={runDuplicateCheck}
                     onChange={(e) => setGuestForm({ ...guestForm, member_number: e.target.value })}
                     data-testid="guest-number" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Email (optional)</label>
              <input type="email" value={guestForm.email} onBlur={runDuplicateCheck}
                     onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                     data-testid="guest-email" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Phone (optional)</label>
              <input value={guestForm.phone} onBlur={runDuplicateCheck}
                     onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                     data-testid="guest-phone" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Locker # (optional)</label>
              <input value={guestForm.locker_number}
                     onChange={(e) => setGuestForm({ ...guestForm, locker_number: e.target.value })}
                     data-testid="guest-locker" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Where Seen (optional)</label>
              <select value={guestForm.where_seen}
                      onChange={(e) => setGuestForm({ ...guestForm, where_seen: e.target.value, where_seen_other: e.target.value === "Other" ? guestForm.where_seen_other : "" })}
                      data-testid="guest-where-seen" className="input-field">
                <option value="">— Select —</option>
                {(options?.where_seen_options || []).map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              {guestForm.where_seen === "Other" && (
                <input value={guestForm.where_seen_other}
                       onChange={(e) => setGuestForm({ ...guestForm, where_seen_other: e.target.value })}
                       placeholder="Where did the member see it?"
                       data-testid="guest-where-seen-other"
                       className="input-field mt-2" />
              )}
            </div>
          </div>
          {duplicates && (duplicates.unregistered?.length > 0 || duplicates.members?.length > 0) && (
            <div className="p-3 border border-[#B59A5A] bg-[#FFFCEF]" data-testid="guest-duplicates">
              <div className="text-xs uppercase tracking-widest text-[#8A6D2E] font-semibold">Possible existing records</div>
              <ul className="mt-2 text-xs text-[#4A4A4A] space-y-1">
                {(duplicates.members || []).map((m) => (
                  <li key={m.user_id}>Registered member: {m.name} · {m.email}</li>
                ))}
                {(duplicates.unregistered || []).map((u) => (
                  <li key={u.unregistered_ref_id}>Prior guest order: {u.member_name || "—"} · #{u.member_number || "—"}</li>
                ))}
              </ul>
              <label className="mt-3 inline-flex items-center gap-2 text-xs">
                <input type="checkbox" checked={ignoreDupes} onChange={(e) => setIgnoreDupes(e.target.checked)} data-testid="ignore-duplicates" />
                Create new record anyway
              </label>
            </div>
          )}
        </div>
      )}

      {orderType === "Golf Shop Order" && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="attribution-golf-shop">
          <div>
            <label className="label-elegant block mb-1">Order For (Purpose)</label>
            <select value={gs.order_purpose} onChange={(e) => setGS({ ...gs, order_purpose: e.target.value })}
                    data-testid="gs-purpose" className="input-field">
              {(options?.order_purposes || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="label-elegant block mb-1">Department</label>
            <input value={gs.department} onChange={(e) => setGS({ ...gs, department: e.target.value })}
                   data-testid="gs-department" className="input-field" />
          </div>
          <div>
            <label className="label-elegant block mb-1">PO Number</label>
            <input value={gs.po_number} onChange={(e) => setGS({ ...gs, po_number: e.target.value })}
                   data-testid="gs-po" className="input-field" placeholder="PO-7821" />
          </div>
          <div>
            <label className="label-elegant block mb-1">Budget Code</label>
            <input value={gs.budget_code} onChange={(e) => setGS({ ...gs, budget_code: e.target.value })}
                   data-testid="gs-budget" className="input-field" placeholder="RETAIL-2027" />
          </div>
          <label className="md:col-span-2 inline-flex items-center gap-2 text-sm text-[#1A1A1A]">
            <input type="checkbox" checked={gs.internal_approval_required}
                   onChange={(e) => setGS({ ...gs, internal_approval_required: e.target.checked })}
                   data-testid="gs-internal-approval" />
            Require internal manager approval before vendor submission
          </label>
        </div>
      )}

      {/* Line items */}
      <div className="mb-6" data-testid="line-items">
        <div className="flex items-center justify-between mb-2">
          <label className="label-elegant">Line Items</label>
          <button type="button" onClick={addItem} data-testid="add-line-item"
                  className="text-xs uppercase tracking-widest text-[#1A422D] hover:text-[#B59A5A] inline-flex items-center gap-1">
            <Plus size={13} /> Add Item
          </button>
        </div>
        <div className="border border-[#E5E3D8] bg-white divide-y divide-[#F0EDE0]">
          {items.map((it, idx) => (
            <div key={idx} className="p-3 grid grid-cols-12 gap-2 items-start" data-testid={`line-item-${idx}`}>
              <div className="col-span-12 md:col-span-4">
                <input placeholder="Item name" value={it.item_name}
                       onChange={(e) => updateItem(idx, { item_name: e.target.value })}
                       data-testid={`line-item-name-${idx}`}
                       className="input-field" />
              </div>
              <div className="col-span-6 md:col-span-3">
                <input placeholder="Brand" value={it.brand || ""}
                       onChange={(e) => updateItem(idx, { brand: e.target.value })}
                       data-testid={`line-item-brand-${idx}`} className="input-field" />
              </div>
              <div className="col-span-6 md:col-span-2">
                <input placeholder="Size" value={it.size}
                       onChange={(e) => updateItem(idx, { size: e.target.value })}
                       data-testid={`line-item-size-${idx}`} className="input-field" />
              </div>
              <div className="col-span-6 md:col-span-2">
                <input placeholder="Color" value={it.color}
                       onChange={(e) => updateItem(idx, { color: e.target.value })}
                       data-testid={`line-item-color-${idx}`} className="input-field" />
              </div>
              <div className="col-span-4 md:col-span-1">
                <input type="number" min="1" placeholder="Qty" value={it.quantity}
                       onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                       data-testid={`line-item-qty-${idx}`} className="input-field" />
              </div>
              <div className="col-span-6 md:col-span-2 md:col-start-6">
                <input type="number" step="0.01" placeholder="Unit $" value={it.unit_cost}
                       onChange={(e) => updateItem(idx, { unit_cost: e.target.value })}
                       data-testid={`line-item-cost-${idx}`} className="input-field" />
              </div>
              <div className="col-span-2 md:col-span-1 flex items-center justify-end gap-2">
                <button type="button" onClick={() => duplicateItem(idx)}
                        data-testid={`line-item-duplicate-${idx}`}
                        title="Duplicate this item"
                        className="text-[#1A422D] hover:text-[#B59A5A]">
                  <Copy size={13} />
                </button>
                {items.length > 1 && (
                  <button type="button" onClick={() => removeItem(idx)}
                          data-testid={`line-item-remove-${idx}`}
                          className="text-[#a33] hover:text-[#7a1717]">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {/* iter68 · Per-line-item decoration & style specs. Optional but
                  always visible so multi-item orders can carry distinct
                  logo/location/color/style per row. */}
              <div className="col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                <input placeholder="Style #" value={it.style_number || ""}
                       onChange={(e) => updateItem(idx, { style_number: e.target.value })}
                       data-testid={`line-item-style-${idx}`} className="input-field text-sm" />
                <input placeholder="Logo (e.g. Club Crest)" value={it.logo_preference || ""}
                       onChange={(e) => updateItem(idx, { logo_preference: e.target.value })}
                       data-testid={`line-item-logo-${idx}`} className="input-field text-sm" />
                <input placeholder="Logo Location" value={it.logo_location || ""}
                       onChange={(e) => updateItem(idx, { logo_location: e.target.value })}
                       data-testid={`line-item-logo-location-${idx}`} className="input-field text-sm" />
                <input placeholder="Logo Color" value={it.logo_color || ""}
                       onChange={(e) => updateItem(idx, { logo_color: e.target.value })}
                       data-testid={`line-item-logo-color-${idx}`} className="input-field text-sm" />
                <input placeholder="Logo Thread Color" value={it.thread_color || ""}
                       onChange={(e) => updateItem(idx, { thread_color: e.target.value })}
                       data-testid={`line-item-thread-color-${idx}`} className="input-field text-sm" />
              </div>

              {/* Per-item photo upload — up to 3 reference photos.
                  iter67 §8 · drag-and-drop + click-to-upload. */}
              <div
                className={`col-span-12 mt-2 pt-3 border-t transition-colors ${dragOverIdx === idx ? "border-[#B59A5A] bg-[#FFFCEF]" : "border-[#F0EDE0]"}`}
                data-testid={`line-item-photos-${idx}`}
                onDragEnter={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                onDragLeave={(e) => {
                  // Only clear when leaving the actual drop-zone bounds.
                  if (e.currentTarget.contains(e.relatedTarget)) return;
                  setDragOverIdx((prev) => (prev === idx ? null : prev));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverIdx(null);
                  if (e.dataTransfer?.files?.length) uploadFilesForItem(idx, e.dataTransfer.files);
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#7A7A7A]">
                    Reference Photos <span className="text-[#B59A5A]">· up to {MAX_PHOTOS_PER_ITEM}</span>
                    <span className="ml-2 normal-case tracking-normal text-[#9A9A9A]">
                      {dragOverIdx === idx ? "Drop to upload" : "drag & drop, or click Add"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRefs.current[idx]?.click()}
                    disabled={(it.photos || []).length >= MAX_PHOTOS_PER_ITEM || uploadingIdx === idx}
                    data-testid={`line-item-photo-upload-${idx}`}
                    className="inline-flex items-center gap-1 text-xs text-[#1A422D] hover:text-[#B59A5A] disabled:opacity-40"
                  >
                    <ImagePlus size={13} />
                    {uploadingIdx === idx ? "Uploading…" : "Add photo"}
                  </button>
                  <input
                    ref={(el) => { fileInputRefs.current[idx] = el; }}
                    type="file"
                    accept="image/*,image/heic,image/heif"
                    multiple
                    hidden
                    data-testid={`line-item-photo-input-${idx}`}
                    onChange={(e) => uploadFilesForItem(idx, e.target.files)}
                  />
                </div>
                {(it.photos || []).length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {(it.photos || []).map((p) => (
                      <div key={p.photo_id} className="relative aspect-square border border-[#E5E3D8] bg-white overflow-hidden">
                        <AuthImage photoId={p.photo_id} alt={p.original_filename || ""} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeItemPhoto(idx, p.photo_id)}
                          data-testid={`line-item-photo-remove-${idx}-${p.photo_id}`}
                          className="absolute top-1 right-1 bg-[#1A422D] text-white text-[9px] tracking-widest uppercase px-1.5 py-0.5"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {runningTotal > 0 && (
          <div className="mt-2 text-xs text-[#4A4A4A]" data-testid="running-total">
            Estimated subtotal: <span className="text-[#1A422D] font-medium">${runningTotal.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Common fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div>
          <label className="label-elegant block mb-1">Category</label>
          <select value={common.category}
                  onChange={(e) => setCommon({ ...common, category: e.target.value })}
                  data-testid="order-category" className="input-field">
            <option value="">— Select —</option>
            {(options?.order_categories || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="label-elegant block mb-1">Subcategory</label>
          <select value={common.subcategory}
                  onChange={(e) => setCommon({ ...common, subcategory: e.target.value })}
                  data-testid="order-subcategory" className="input-field">
            <option value="">— Select —</option>
            {(options?.order_subcategories || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="label-elegant block mb-1">Requested Delivery Date</label>
          <input type="date" value={common.requested_delivery_date}
                 onChange={(e) => setCommon({ ...common, requested_delivery_date: e.target.value })}
                 data-testid="delivery-date" className="input-field" />
        </div>
        <div>
          <label className="label-elegant block mb-1">Submission</label>
          <select value={common.submission_mode}
                  onChange={(e) => setCommon({ ...common, submission_mode: e.target.value })}
                  data-testid="submission-mode" className="input-field">
            <option value="submit_on_behalf">Submit now</option>
            <option value="quick">Quick Save (needs research)</option>
            <option value="draft">Save as draft</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="label-elegant block mb-1">Vendor Notes (visible on packet)</label>
          <textarea rows={2} value={common.vendor_notes}
                    onChange={(e) => setCommon({ ...common, vendor_notes: e.target.value })}
                    data-testid="vendor-notes" className="input-field" />
        </div>
        <div className="md:col-span-2">
          <label className="label-elegant block mb-1">Internal Staff Note (private)</label>
          <textarea rows={2} value={common.staff_internal_note}
                    onChange={(e) => setCommon({ ...common, staff_internal_note: e.target.value })}
                    data-testid="internal-note" className="input-field" />
        </div>
        <label className="md:col-span-2 inline-flex items-start gap-2 text-sm text-[#1A1A1A] p-3 bg-[#F9F8F4] border border-[#F0EDE0]">
          <input type="checkbox" className="mt-0.5"
                 checked={common.include_member_info_on_packet}
                 onChange={(e) => setCommon({ ...common, include_member_info_on_packet: e.target.checked })}
                 data-testid="include-member-info-on-packet" />
          <span>
            <span className="font-medium">Include Member Name and Locker Number on Vendor Packet</span>
            <span className="block text-xs text-[#7A7A7A] mt-0.5">
              When unchecked, the packet excludes all member-identifying information.
              Only item and ordering details will be shared with the vendor.
            </span>
          </span>
        </label>
      </div>

      {error && <div className="mb-4 text-xs text-[#a33]" data-testid="create-order-error">{error}</div>}

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onBack} data-testid="create-order-cancel"
                className="btn-secondary text-xs">Cancel</button>
        <button type="button" onClick={submit} disabled={busy || !canSubmit()} data-testid="create-order-submit"
                className="btn-primary">
          {busy ? "Creating…" : "Create Order"}
        </button>
      </div>
    </div>
  );
}
